import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCardText,
  MDBCardTitle,
  MDBCol,
  MDBContainer,
  MDBInput,
  MDBRow,
} from "mdb-react-ui-kit";

import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { validatePasswordStrength } from "Projects/SSO_single_sign_on_project/Utils/PasswordUtils";
import { sanitizeSameOriginDestination } from "Projects/CC_cubi_clan_project/Utils/AuthNavigation";

export default function ResetPasswordView() {
  const [searchParams] = useSearchParams();

  const token = useMemo(() => (searchParams.get("token") || "").trim(), [searchParams]);
  const appCode = useMemo(() => (searchParams.get("appCode") || "SSO").trim(), [searchParams]);

  const [loadingToken, setLoadingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");

  useEffect(() => {
    const validate = async () => {
      setLoadingToken(true);
      setError("");

      if (!token) {
        setTokenValid(false);
        setError("El enlace de recuperación no contiene token.");
        setLoadingToken(false);
        return;
      }

      try {
        const requestOptions = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ token }),
        };

        const response = await doFetch("SSO", "validateResetToken", requestOptions);
        const data = await response.json();

        if (data?.STATE === "SUCCESS" && data?.DATA?.valid) {
          setTokenValid(true);
          setRedirectUrl(sanitizeSameOriginDestination(data?.DATA?.redirectUrl) || "");
        } else {
          setTokenValid(false);
          setError(data?.MESSAGE || "El token es inválido o ha caducado.");
        }
      } catch (fetchError) {
        setTokenValid(false);
        setError("No se pudo validar el token.");
        console.error(fetchError);
      } finally {
        setLoadingToken(false);
      }
    };

    validate();
  }, [token]);

  const submitReset = async (event) => {
    event.preventDefault();
    setError("");

    const strengthError = validatePasswordStrength(password);
    if (strengthError) {
      setError(strengthError);
      return;
    }

    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    setSaving(true);

    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          password,
          confirmPassword,
        }),
      };

      const response = await doFetch("SSO", "resetPassword", requestOptions);
      const data = await response.json();

      if (data?.STATE === "SUCCESS") {
        setSuccess(true);
        const safeRedirect = sanitizeSameOriginDestination(data?.DATA?.redirectUrl) || redirectUrl;
        setRedirectUrl(safeRedirect);
        setTimeout(() => {
          if (safeRedirect) {
            window.location.assign(safeRedirect);
          }
        }, 1500);
      } else {
        setError(data?.MESSAGE || "No se pudo restablecer la contraseña.");
      }
    } catch (fetchError) {
      setError("No se pudo contactar con el servidor.");
      console.error(fetchError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <MDBContainer className="py-5">
      <MDBRow className="justify-content-center">
        <MDBCol md="8" lg="6" xl="5">
          <MDBCard>
            <MDBCardBody className="p-4">
              <MDBCardTitle className="mb-3">Restablecer contraseña</MDBCardTitle>
              <MDBCardText className="text-muted mb-4">
                App destino: <strong>{appCode || "SSO"}</strong>
              </MDBCardText>

              {loadingToken ? <div className="alert alert-info py-2">Validando token...</div> : null}
              {!loadingToken && error ? <div className="alert alert-danger py-2">{error}</div> : null}
              {success ? (
                <div className="alert alert-success py-2">
                  Contraseña actualizada correctamente. Serás redirigido en unos segundos.
                </div>
              ) : null}

              {!loadingToken && tokenValid && !success ? (
                <form onSubmit={submitReset}>
                  <div className="mb-3">
                    <MDBInput
                      id="new-password"
                      type="password"
                      label="Nueva contraseña"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <MDBInput
                      id="confirm-password"
                      type="password"
                      label="Confirmar contraseña"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                    />
                  </div>

                  <MDBBtn type="submit" block disabled={saving}>
                    {saving ? "Guardando..." : "Guardar nueva contraseña"}
                  </MDBBtn>
                </form>
              ) : null}

              <div className="mt-3 text-center">
                {redirectUrl ? (
                  <MDBBtn size="sm" color="light" onClick={() => window.location.assign(redirectUrl)}>
                    Ir a la app
                  </MDBBtn>
                ) : (
                  <Link to="/">Volver al login</Link>
                )}
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>
    </MDBContainer>
  );
}
